/**
 * 承認事項の表。見出し行の選択で表示中の行をまとめて選ぶ。
 *
 * 行の描画は `ApprovalRow` が持ち、ここは見出しと行の並びだけを描く。行の鍵は「案件 : ノード」
 * である（`approvalRowKey`）— 同じノードを複数の案件が触りうるので、ノードの id だけでは
 * 別の案件の行が一緒に選ばれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認事項を 3 列の表で描き、見出し行に指を載せたときに出る選択で表示中の行をまとめて選ぶ。
 * @graph-connects ApprovalRow [calls] 承認事項 1 件の行
 */
import type { ApprovalRow as ApprovalItem } from '@cortex/domain-spec-console';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { useTranslation } from 'react-i18next';

import { approvalRowKey } from '../approvals-selection.js';
import { APPROVAL_CELL_CLASS, ApprovalRow, isSelectableApproval } from './approval-row.js';

/** `ApprovalTable` の props。 @graph-connects none */
export interface ApprovalTableProps {
  items: readonly ApprovalItem[];
  selected: ReadonlySet<string>;
  onToggle: (rowKey: string, selected: boolean) => void;
  onToggleAll: (selected: boolean) => void;
}

/**
 * 承認事項の表。見出し行の選択で表示中の行をまとめて選ぶ。
 *
 * 幅の狭い画面では表ごと横に流す。列を落として折り返すと、PJ と対象と状態を 1 行で読めなく
 * なり、横断して比べるという画面の目的が崩れる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認事項を 3 列の表で描き、見出し行に指を載せたときに出る選択で表示中の行をまとめて選ぶ。
 * @graph-connects ApprovalRow [calls] 承認事項 1 件の行
 */
export function ApprovalTable({ items, selected, onToggle, onToggleAll }: ApprovalTableProps) {
  const { t } = useTranslation();
  /* 全選択が指すのは選べる行（未承認）だけである。判断済みの行は選べない。 */
  const selectable = items.filter(isSelectableApproval);
  const selectedShown = selectable.filter((item) => selected.has(approvalRowKey(item))).length;
  const allSelected = selectable.length > 0 && selectedShown === selectable.length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm text-[var(--color-text-primary)]">
        <thead>
          <tr className="group border-b border-[var(--color-border)] text-left text-xs text-[var(--color-text-muted)]">
            <th className={`${APPROVAL_CELL_CLASS} w-8`}>
              {/* 全部選ぶ箱は見出し行に指を載せたとき（とキーボードで移ったとき）だけ見せる。 */}
              <span
                data-approval-select-all
                className="opacity-0 group-hover:opacity-100 focus-within:opacity-100"
              >
                <Checkbox
                  aria-label={t('approvals.selectAll')}
                  checked={allSelected}
                  disabled={selectable.length === 0}
                  onChange={(event) => {
                    onToggleAll(event.target.checked);
                  }}
                />
              </span>
            </th>
            <th className={`${APPROVAL_CELL_CLASS} font-medium`}>{t('approvals.colOperation')}</th>
            <th className={`${APPROVAL_CELL_CLASS} font-medium`}>{t('approvals.colTarget')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ApprovalRow
              key={approvalRowKey(item)}
              item={item}
              selected={selected.has(approvalRowKey(item))}
              onToggle={onToggle}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
